import {
  logError,
  logWarn,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import { sonarUpdateStatus } from '../flights/itineraries/sonarV3UpdateStatusMapper';

import { mapSonarV3ItemToConductorAgent } from './mapSonarItemToConductorAgent';

import type {
  SkyscannerSonarCommonV1Enums,
  SkyscannerSonarV3Response,
} from '@skyscanner-internal/sonar-web-http-client';
import type { SonarV3PayloadResponseSuccess } from '@web-platform/shared-universal-types/src/network/types';
import type { AgentsBookingPanel } from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/agentsBookingPanel';

type AgentIdStatus = {
  agent: SkyscannerSonarV3Response.Agent;
  updateStatus: SkyscannerSonarCommonV1Enums.UpdateStatus | undefined;
};

export const mapAgentsFromSonarV3Response = (
  agentsState: AgentsBookingPanel,
  sonarResponse: SonarV3PayloadResponseSuccess['body'],
) => {
  try {
    const updatedAgentsFromSonarV3 = mapUpdatedSonarV3Agents(sonarResponse.itinerary);

    if (!sonarResponse.pollingCompleted) {
      return updatedAgentsFromSonarV3;
    }

    return {
      ...mapAgentsInStateToCurrent(agentsState),
      ...updatedAgentsFromSonarV3,
    };
  } catch (error) {
    logError({
      component: 'mapSonarV3Agents',
      message: 'Error reducing sonar V3 agents',
      error,
    });
    return agentsState;
  }
};

const mapAgentsInStateToCurrent = (agentsState: AgentsBookingPanel) =>
  Object.keys(agentsState).reduce<AgentsBookingPanel>(
    (agentsById, agentId: string) => ({
      ...agentsById,
      [agentId]: {
        ...agentsState[agentId],
        update_status: 'current',
      },
    }),
    {},
  );

const doesAgentIdStatusListAlreadyContainsAgent = (
  agentIdStatuses: AgentIdStatus[],
  agent: SkyscannerSonarV3Response.Agent,
) => agentIdStatuses.some((item: AgentIdStatus) => item.agent.id === agent.id);

// an agent can have multiple pricing items with different statuses - if any is pending, then the agent is considered pending
const getMostRelevantPricingItemStatusForAgent = (
  allPricingItems: SkyscannerSonarV3Response.PricingItem[],
  agent: SkyscannerSonarV3Response.Agent,
  defaultStatus: SkyscannerSonarCommonV1Enums.UpdateStatus | undefined,
) =>
  allPricingItems.some(
    (item: SkyscannerSonarV3Response.PricingItem) =>
      item.agent?.id === agent.id && item.updateStatus === sonarUpdateStatus.UPDATE_STATUS_PENDING,
  )
    ? sonarUpdateStatus.UPDATE_STATUS_PENDING
    : defaultStatus;

const getDistinctAgentStatuses = (pricingOptions?: SkyscannerSonarV3Response.PricingOption[]) => {
  const allPricingItems = (pricingOptions || []).flatMap((pricingOption) =>
    (pricingOption.pricingItems || []).map((pricingItem) => pricingItem),
  );
  return allPricingItems.reduce<AgentIdStatus[]>((agentIdStatuses, pricingItem) => {
    if (
      !pricingItem.agent ||
      doesAgentIdStatusListAlreadyContainsAgent(agentIdStatuses, pricingItem.agent)
    ) {
      return agentIdStatuses;
    }

    return [
      ...agentIdStatuses,
      {
        agent: pricingItem.agent,
        updateStatus: getMostRelevantPricingItemStatusForAgent(
          allPricingItems,
          pricingItem.agent,
          pricingItem.updateStatus,
        ),
      },
    ];
  }, []);
};

const mapUpdatedSonarV3Agents = (itinerary?: SkyscannerSonarV3Response.Itinerary) =>
  getDistinctAgentStatuses(itinerary?.pricingOptions).reduce<AgentsBookingPanel>(
    (agentsById, item) => {
      try {
        const agentId = item.agent?.id;

        if (agentId) {
          return {
            ...agentsById,
            [agentId]: mapSonarV3ItemToConductorAgent(item),
          };
        }

        logWarn({
          component: 'mapUpdatedSonarV3Agents',
          message: `SONARV3: Received an agent that is missing an id`,
          data: JSON.stringify(item),
        });

        return agentsById;
      } catch (error) {
        logError({
          component: 'mapUpdatedSonarV3Agents',
          error,
          message: `SONARV3: ${error.message}`,
          data: JSON.stringify(item),
        });

        return agentsById;
      }
    },
    {},
  );
